import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Subject, finalize, takeUntil } from 'rxjs';
import { AskpodService } from '../../../../services/api/askpod.service';
import { AskpodAddonPlan } from '../../../../models/askpod.model';
import {
    AskPodTrialEndedPackage,
    AskPodTrialEndedPopupInterface,
    AskPodTrialEndedPopupModel,
} from './askpod-trial-ended-popup.model';

/** Category value that flags an add-on plan as a paid package (as opposed to trial/free). */
const PAID_CATEGORY = 'PAID';

@Component({
    selector: 'app-askpod-trial-ended-popup',
    templateUrl: './askpod-trial-ended-popup.component.html',
})
export class AskPodTrialEndedPopupComponent implements OnInit, OnDestroy {
    data: AskPodTrialEndedPopupModel;

    /** Paid packages to display, cheapest first with the priciest tagged "Best Value". */
    packages: AskPodTrialEndedPackage[] = [];

    /** True while the catalogue request is in flight. */
    loading = true;

    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) popupData: AskPodTrialEndedPopupInterface,
        private askpodService: AskpodService,
    ) {
        this.data = new AskPodTrialEndedPopupModel(popupData || ({} as AskPodTrialEndedPopupInterface));
    }

    ngOnInit(): void {
        this.askpodService
            .getAddonPlans()
            .pipe(
                finalize(() => (this.loading = false)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (response) => {
                    this.packages = this.toPackages(response?.result?.data ?? []);
                },
                // On failure leave the list empty; the sales note still guides the user.
                error: () => (this.packages = []),
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Keeps only paid plans, orders them cheapest → priciest, and tags the single
     * highest-priced plan as "Best Value". Plans without a price sort first and can
     * never win the "Best Value" tag.
     */
    private toPackages(plans: AskpodAddonPlan[]): AskPodTrialEndedPackage[] {
        const paidPlans = plans
            .filter((plan) => (plan.category ?? '').trim().toUpperCase() === PAID_CATEGORY)
            .sort((a, b) => (a.priceMyr ?? -Infinity) - (b.priceMyr ?? -Infinity));

        // Highest priced plan across the paid set earns the "Best Value" tag.
        let bestValuePlan: AskpodAddonPlan | undefined;
        for (const plan of paidPlans) {
            if (plan.priceMyr == null) {
                continue;
            }
            if (bestValuePlan == null || plan.priceMyr > (bestValuePlan.priceMyr ?? -Infinity)) {
                bestValuePlan = plan;
            }
        }

        return paidPlans.map((plan) => ({
            addonCode: plan.addonCode,
            name: plan.displayName,
            dailyCredits: plan.dailyCredits,
            price: plan.priceMyr,
            isBestValue: plan === bestValuePlan,
        }));
    }
}

// Open askPOD "Trial Has Ended" modal box
export function openAskPodTrialEndedDialog(
    dialog: MatDialog,
    popupData?: AskPodTrialEndedPopupInterface,
): MatDialogRef<AskPodTrialEndedPopupComponent, boolean> {
    const config = new MatDialogConfig();

    config.autoFocus = false;
    config.disableClose = false;
    config.maxWidth = '32.5rem';
    config.width = '90vw';
    config.panelClass = 'askpod-trial-ended-dialog';
    config.data = { ...popupData };

    return dialog.open(AskPodTrialEndedPopupComponent, config);
}
